// Bloom Booking-Flow UI-Bausteine
// Übersetzt aus /Truambeauty (1)/flow-chrome.jsx + flow-steps.jsx (Bloom/Salon-Variante)
// Nutzt adminTheme.ts Tokens; ergänzt lokale Konstanten für pill-radius + onAccent
import React from 'react';
import { T, rgba } from '../../../utils/adminTheme';

// Lokale Ergänzungen zu T.r (adminTheme hat aktuell kein pill)
const PILL = 999;
const ON_ACCENT = '#ffffff';

// ─── BloomPill ─────────────────────────────────────────────────────────────
// Filter-/Choice-Pill mit active-State, optional dense
export const BloomPill: React.FC<{
  children: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
  dense?: boolean;
}> = ({ children, active, onClick, dense }) => (
  <button onClick={onClick} style={{
    padding: dense ? '6px 12px' : '8px 16px',
    background: active ? T.text : T.surface,
    color: active ? T.bg : T.text,
    border: `1px solid ${active ? T.text : T.line}`,
    borderRadius: PILL,
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    fontFamily: T.body,
    fontSize: dense ? 12 : 13,
    fontWeight: 500,
    transition: 'all .15s',
  }}>{children}</button>
);

// ─── BubbleInput ──────────────────────────────────────────────────────────
// Text-Input mit Label-im-Bubble (Form-Felder im Booking-Flow)
export const BubbleInput: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  onBlur?: () => void;
  error?: string;
}> = ({ label, value, onChange, placeholder, type = 'text', onBlur, error }) => (
  <div style={{
    background: T.surface,
    border: `1px solid ${error ? '#e53935' : T.line}`,
    borderRadius: T.r.lg,
    padding: '10px 18px',
    transition: 'border-color .15s',
  }}>
    <div style={{
      fontFamily: T.body,
      fontSize: 11,
      color: error ? '#e53935' : T.muted,
      marginBottom: 2,
      letterSpacing: '0.06em',
    }}>{error ?? label}</div>
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      onBlur={onBlur}
      placeholder={placeholder}
      style={{
        width: '100%',
        padding: 0,
        background: 'transparent',
        border: 'none',
        fontFamily: T.body,
        fontSize: 16,
        color: T.text,
        outline: 'none',
      }}
    />
  </div>
);

// ─── SummaryRow ───────────────────────────────────────────────────────────
// Key/Value-Zeile, optional bold (italic serif für Value)
export const SummaryRow: React.FC<{
  k: string;
  v: React.ReactNode;
  bold?: boolean;
}> = ({ k, v, bold }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: 8,
  }}>
    <div style={{
      fontFamily: T.body,
      fontSize: 12,
      color: T.muted,
      letterSpacing: '0.02em',
    }}>{k}</div>
    <div style={{
      fontFamily: bold ? T.display : T.body,
      fontStyle: bold ? 'italic' : 'normal',
      fontSize: bold ? 18 : 13,
      color: T.text,
      textAlign: 'right',
      flex: 1,
    }}>{v}</div>
  </div>
);

// ─── Hint ─────────────────────────────────────────────────────────────────
// Sanfte Hinweis-Box (paper bg, lineSoft border)
export const Hint: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{
    padding: '14px 16px',
    background: T.paper,
    borderRadius: T.r.md,
    border: `1px solid ${T.lineSoft}`,
    fontFamily: T.body,
    fontSize: 13,
    color: T.muted,
    lineHeight: 1.5,
  }}>{children}</div>
);

// ─── EmptyState ───────────────────────────────────────────────────────────
// Zentrierter Empty-State mit Glyph
export const EmptyState: React.FC<{
  title: string;
  hint?: string;
  glyph?: string;
}> = ({ title, hint, glyph = '✿' }) => (
  <div style={{
    padding: '40px 24px',
    textAlign: 'center',
    background: T.paper,
    borderRadius: T.r.lg,
    border: `1px dashed ${T.line}`,
  }}>
    <div style={{
      fontSize: 36,
      color: T.accent,
      marginBottom: 10,
    }}>{glyph}</div>
    <div style={{
      fontFamily: T.display,
      fontStyle: 'italic',
      fontSize: 20,
      color: T.text,
      marginBottom: 6,
    }}>{title}</div>
    {hint && (
      <div style={{ fontFamily: T.body, fontSize: 13, color: T.muted }}>{hint}</div>
    )}
  </div>
);

// ─── PhotoSlot ────────────────────────────────────────────────────────────
// React-Variante des <image-slot> Web Components der Vorlage.
// Akzeptiert src (Bild-URL) oder zeigt Placeholder.
export const PhotoSlot: React.FC<{
  src?: string;
  alt?: string;
  height?: number;
  placeholder?: string;
  shape?: 'rounded' | 'circle' | 'pill' | 'rect';
  radius?: number;
}> = ({ src, alt = '', height = 120, placeholder = 'Foto', shape = 'rounded', radius = 18 }) => {
  const borderRadius =
    shape === 'circle' ? '50%' :
    shape === 'pill' ? PILL :
    shape === 'rect' ? 0 :
    radius;
  return (
    <div style={{
      width: '100%',
      height,
      borderRadius,
      background: T.surface2,
      border: `1px dashed ${T.line}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      color: T.muted,
      fontFamily: T.body,
      fontSize: 12,
    }}>
      {src
        ? <img src={src} alt={alt} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : placeholder}
    </div>
  );
};

// ─── BloomProgress ────────────────────────────────────────────────────────
// Pill-Progress: kleine dots, aktiver Step größer mit Step-Nummer
export type ProgressStep = { key: string; num: string };

export const BloomProgress: React.FC<{
  steps: ProgressStep[];
  stepIdx: number;
}> = ({ steps, stepIdx }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
    {steps.map((s, i) => {
      const done = i < stepIdx;
      const active = i === stepIdx;
      const size = active ? 26 : 9;
      return (
        <React.Fragment key={s.key}>
          <div style={{
            width: size,
            height: size,
            borderRadius: PILL,
            background: done ? T.text : (active ? T.accent : T.lineSoft),
            border: active ? `1px solid ${T.accent}` : 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.35s cubic-bezier(.2,.7,.3,1)',
            fontFamily: T.display,
            fontStyle: 'italic',
            fontSize: 13,
            color: active ? ON_ACCENT : 'transparent',
            flex: '0 0 auto',
          }}>{active ? s.num.slice(1) : ''}</div>
          {i < steps.length - 1 && (
            <div style={{
              flex: 1,
              height: 1,
              background: i < stepIdx ? T.text : T.lineSoft,
              transition: 'background .3s',
            }} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

// ─── BloomTopBar ──────────────────────────────────────────────────────────
// Sticky Header mit Logo, Back-Button, optionalem CartBadge, Progress + Step-Prompt
export const BloomTopBar: React.FC<{
  steps: ProgressStep[];
  stepIdx: number;
  prompt: string;
  onBack?: () => void;
  brand?: string;
  cartCount?: number;
  showCart?: boolean;
}> = ({ steps, stepIdx, prompt, onBack, brand = 'Traum Beauty Cakar', cartCount = 0, showCart }) => {
  const backDisabled = stepIdx === 0;
  return (
    <div style={{
      padding: '60px 22px 18px',
      background: T.bg,
      position: 'sticky',
      top: 0,
      zIndex: 5,
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 16,
      }}>
        <button
          onClick={backDisabled ? undefined : onBack}
          disabled={backDisabled}
          style={{
            width: 38, height: 38,
            borderRadius: PILL,
            background: T.surface,
            border: `1px solid ${T.line}`,
            cursor: backDisabled ? 'default' : 'pointer',
            opacity: backDisabled ? 0.3 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: T.text,
            padding: 0,
          }}
        >
          <svg width="16" height="14" viewBox="0 0 16 14" fill="none">
            <path d="M6 1L1 7l5 6M1 7h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>
        <div style={{
          fontFamily: T.display,
          fontSize: 18,
          fontStyle: 'italic',
          color: T.text,
          letterSpacing: '0.01em',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}>
          <span style={{ color: T.accent, fontSize: 22, lineHeight: 1 }}>✿</span>
          {brand}
        </div>
        <div style={{ width: 38, height: 38, position: 'relative' }}>
          {showCart && cartCount > 0 && (
            <div style={{
              position: 'absolute', right: 0, top: 0,
              width: 38, height: 38, borderRadius: '50%',
              background: T.text, color: T.bg,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: T.display, fontStyle: 'italic', fontSize: 18,
              boxShadow: `0 6px 18px ${rgba(T.text, 0.25)}`,
            }}>{cartCount}</div>
          )}
        </div>
      </div>
      <BloomProgress steps={steps} stepIdx={stepIdx} />
      <div style={{
        fontFamily: T.display,
        fontSize: 30,
        lineHeight: '1.05',
        color: T.text,
        fontStyle: 'italic',
        letterSpacing: '-0.01em',
        marginTop: 18,
      }}>{prompt}</div>
    </div>
  );
};

// ─── BloomBottomCTA ───────────────────────────────────────────────────────
// Sticky Footer mit Primary-Button, optionaler Summary-Card, optionalem Secondary-Link
export const BloomBottomCTA: React.FC<{
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  summary?: { label: string; value: string };
  secondary?: { label: string; onClick: () => void };
}> = ({ label, onClick, disabled, summary, secondary }) => (
  <div style={{
    position: 'sticky',
    bottom: 0,
    background: `linear-gradient(180deg, transparent, ${T.bg} 30%)`,
    padding: '24px 22px 40px',
    zIndex: 5,
  }}>
    {summary && (
      <div style={{
        background: T.surface,
        padding: '12px 18px',
        borderRadius: T.r.lg,
        marginBottom: 12,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        border: `1px solid ${T.line}`,
      }}>
        <div style={{ fontFamily: T.body, fontSize: 12, color: T.muted }}>{summary.label}</div>
        <div style={{
          fontFamily: T.display,
          fontSize: 22,
          fontStyle: 'italic',
          color: T.text,
        }}>{summary.value}</div>
      </div>
    )}
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        width: '100%',
        padding: '18px',
        background: disabled ? T.muted : T.accent,
        color: ON_ACCENT,
        border: 'none',
        borderRadius: PILL,
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontFamily: T.body,
        fontSize: 15,
        fontWeight: 600,
        letterSpacing: '0.02em',
        opacity: disabled ? 0.5 : 1,
        boxShadow: disabled ? 'none' : `0 14px 36px ${rgba(T.accent, 0.35)}`,
        transition: 'all .2s cubic-bezier(.2,.7,.3,1)',
      }}
    >{label}</button>
    {secondary && (
      <button
        onClick={secondary.onClick}
        style={{
          width: '100%',
          marginTop: 10,
          padding: '10px',
          background: 'transparent',
          color: T.muted,
          border: 'none',
          cursor: 'pointer',
          fontFamily: T.display,
          fontStyle: 'italic',
          fontSize: 14,
        }}
      >{secondary.label}</button>
    )}
  </div>
);
